import { z } from 'zod';

const selectorList = z.array(z.string().trim().min(1).max(500)).min(1).max(50);

export const XhsNoteSelectorSet = z
  .object({
    title: selectorList,
    body: selectorList,
    authorName: selectorList,
    authorExternalId: selectorList,
    publishedAt: selectorList,
    tags: selectorList,
    likes: selectorList,
    collects: selectorList,
    comments: selectorList,
    shares: selectorList,
    images: selectorList,
    videos: selectorList,
  })
  .strict();

const routeMatcher = z
  .object({
    pathname: z.string().startsWith('/').max(256),
    match: z.enum(['exact', 'prefix']),
  })
  .strict();

const requestAdapter = z
  .object({
    method: z.literal('GET'),
    pathname: z.string().startsWith('/api/galaxy/').max(256),
  })
  .strict();

export const XhsPlatformRuntimeConfig = z
  .object({
    schema_version: z.literal(1),
    version: z.string().trim().min(1).max(80),
    config_version: z.string().trim().min(1).max(80),
    platform: z.literal('xhs'),
    enabled: z.boolean(),
    min_extension_version: z.string().regex(/^\d+\.\d+\.\d+$/),
    allowed_origins: z.tuple([z.literal('https://creator.xiaohongshu.com')]).readonly(),
    pages: z
      .object({
        note_manager: z
          .object({
            enabled: z.boolean(),
            routes: z.array(routeMatcher).min(1).max(20),
            observe_urls: z.array(z.string().startsWith('/api/galaxy/').max(256)).max(20),
            injection: z
              .object({
                kind: z.literal('side_floating'),
                side: z.enum(['left', 'right']),
                offset_px: z.number().int().min(8).max(240),
              })
              .strict(),
            dom: z.record(z.string().min(1).max(80), selectorList),
          })
          .strict(),
        note_detail: z
          .object({
            enabled: z.boolean(),
            routes: z.array(routeMatcher).min(1).max(20),
            dom: z
              .object({
                selector_sets: z.array(XhsNoteSelectorSet).min(1).max(20),
              })
              .strict(),
          })
          .strict(),
      })
      .strict(),
    requests: z
      .object({
        creator_identity: requestAdapter,
        creator_personal: requestAdapter,
        published_notes: requestAdapter,
        account_metrics: requestAdapter,
      })
      .strict(),
  })
  .strict()
  .superRefine((config, context) => {
    if (config.version !== config.config_version) {
      context.addIssue({
        code: 'custom',
        path: ['version'],
        message: 'version must equal config_version',
      });
    }
  });

export type XhsPlatformRuntimeConfig = z.infer<typeof XhsPlatformRuntimeConfig>;

export const PlatformRuntimeConfig = z.discriminatedUnion('platform', [XhsPlatformRuntimeConfig]);
export type PlatformRuntimeConfig = z.infer<typeof PlatformRuntimeConfig>;

export function matchesConfiguredRoute(
  config: Pick<XhsPlatformRuntimeConfig, 'enabled' | 'pages'>,
  pathname: string,
): boolean {
  const page = config.pages.note_manager;
  return (
    config.enabled &&
    page.enabled &&
    page.routes.some((route) =>
      route.match === 'exact' ? pathname === route.pathname : pathname.startsWith(route.pathname),
    )
  );
}

export function compareSemver(left: string, right: string): number {
  const a = left.split('.').map(Number);
  const b = right.split('.').map(Number);
  for (let index = 0; index < 3; index += 1) {
    const difference = (a[index] ?? 0) - (b[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}
